"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";
import { DOMAIN } from "shared/SharedConstants";
import Chip from "src/components/common/Chip/Chip";
import type { ThemeColor } from "src/lib/FrontendTypes";
import { PLATFORM_STATUS_PATH, type PlatformState } from "src/lib/PlatformStatus";
import { useExtracted } from "#i18n";
import styles from "./PlatformStatusChip.module.scss";

const STATE_COLORS: Record<PlatformState, ThemeColor> = {
  operational: "success",
  degraded: "warning",
  maintenance: "warning",
  downtime: "danger",
  unknown: "neutral",
};

/** How often an open page checks the state again. */
const REFRESH_MS = 5 * 60_000;

export interface PlatformStatusChipProps {
  className?: string;
  /**
   * The state the server rendered with (the portal layout's data). Without it (the docs, whose
   * documents are cached for everyone), the chip keeps its space empty, loads the state after
   * hydration and fades in; it stays hidden if the state can't be loaded.
   */
  initialState?: PlatformState;
}

/**
 * A small chip with the platform's overall state, linking to the status page. It checks the state
 * again every few minutes; a failed check keeps the last known state.
 */
export default function PlatformStatusChip({ className, initialState }: PlatformStatusChipProps) {
  const t = useExtracted();
  // Null until a state is known, when the server didn't render one.
  const [state, setState] = useState<PlatformState | null>(initialState ?? null);
  useEffect(() => {
    const controller = new AbortController();
    const refresh = () =>
      fetch(PLATFORM_STATUS_PATH, { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : { state: "unknown" }))
        .then((body: { state?: PlatformState }) => {
          const next = body.state ?? "unknown";
          // An unknown result never replaces a known state, and never appears on its own.
          setState((current) =>
            next === "unknown" ? (current ?? (initialState ? next : null)) : next,
          );
        })
        .catch(() => {});
    if (initialState === undefined) void refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [initialState]);
  const shown = state ?? "operational";
  const label = {
    operational: t("Operational"),
    degraded: t("Degraded"),
    downtime: t("Disruption"),
    maintenance: t("Maintenance"),
    unknown: t("Status"),
  }[shown];
  const description = {
    operational: t("All systems operational"),
    degraded: t("Some systems degraded"),
    downtime: t("Service disruption"),
    maintenance: t("Scheduled maintenance"),
    unknown: t("Platform status"),
  }[shown];

  // The space it will take stays reserved while the state loads, so nothing moves when it appears.
  if (state === null)
    return (
      <span aria-hidden="true" className={clsx(styles.pending, className)}>
        <Chip color="neutral">{label}</Chip>
      </span>
    );

  return (
    <a
      href={`https://status.${DOMAIN}`}
      target="_blank"
      rel="noreferrer"
      data-state={state}
      aria-label={t("{status} (opens the status page in a new tab)", { status: description })}
      className={clsx(
        styles.link,
        initialState === undefined && styles.fadeIn,
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        className,
      )}
    >
      <Chip color={STATE_COLORS[state]}>{label}</Chip>
    </a>
  );
}
